// Searchable country multiselect — styled to match the Select component from @startus/ui
// Searches by ISO code or country name, uses fixed-position dropdown to escape overflow:hidden
import { useState, useRef, useEffect, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFormMode } from '../../hooks/useFormMode';
import { FieldWrapper, useListboxNavigation } from '@startus/ui';
import type { CSSProperties } from 'react';
import type { BaseFieldProps, FieldOption } from '../../types/fields';

function Pill({ label, onRemove }: { label: string; onRemove?: () => void }) {
  const { t } = useTranslation('common');
  return (
    <span
      className="badge badge-primary gap-2 py- rounded-full flex max-w-[220px] items-center"
      title={label}
    >
      <span className="truncate">{label}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="hover:opacity-70 border rounded-full shrink-0"
          aria-label={t('dropdown.remove', { label })}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3 w-3"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </span>
  );
}

interface CountryMultiselectEditorProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: FieldOption[];
  labelFor: (iso: string) => string;
}

// Edit mode — its own component so the dropdown hooks don't live inside Controller's render prop.
function CountryMultiselectEditor({
  values,
  onChange,
  options,
  labelFor,
}: CountryMultiselectEditorProps) {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [filtered, setFiltered] = useState<FieldOption[]>([]);

  const applyFilter = useCallback(
    (query: string, selected: string[]) => {
      const q = query.toLowerCase();
      const result = options
        .filter((o) => {
          if (selected.includes(o.value)) return false;
          if (!q) return true;
          return o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q);
        })
        .slice(0, 100);
      setFiltered(result);
    },
    [options]
  );

  // Recompute fixed dropdown position from trigger rect on open, and keep it
  // tracking the trigger while scrolling/resizing — the position was
  // previously computed once on open, so the menu stayed anchored to the
  // viewport instead of following the (scrolled-away) trigger.
  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, { capture: true, passive: true });
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, { capture: true });
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!containerRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleOpen = () => {
    setIsOpen(true);
    applyFilter('', values);
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  // Multi-pick: the list stays open and the search keeps focus for the next pick.
  const handleSelect = (iso: string) => {
    onChange([...values, iso]);
    if (searchRef.current) searchRef.current.value = '';
    applyFilter('', [...values, iso]);
    listbox.resetActive();
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  const handleClose = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const listbox = useListboxNavigation({
    items: filtered,
    isOpen,
    onSelect: (o) => handleSelect(o.value),
    onClose: handleClose,
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyFilter(e.target.value, values);
    listbox.resetActive();
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Trigger — matches Select component styling */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="input w-full rounded-lg bg-[#3A3A3A] border border-white/10 focus:outline-none focus:border-white/20 text-left flex items-center justify-between"
      >
        <span className="text-[#CACACA] truncate">{t('common.select')}</span>
      </button>

      {/* Selected pills — below the trigger */}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {values.map((iso) => (
            <Pill
              key={iso}
              label={labelFor(iso)}
              onRemove={() => onChange(values.filter((v) => v !== iso))}
            />
          ))}
        </div>
      )}

      {/* Fixed-position dropdown — escapes overflow:hidden */}
      {isOpen && (
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="fixed z-[9999] bg-base-100 border border-base-300 rounded-lg shadow-lg flex flex-col"
        >
          <div className="p-2 border-b border-base-200">
            <input
              ref={searchRef}
              type="text"
              defaultValue=""
              {...listbox.getComboboxProps()}
              onChange={handleSearchChange}
              onMouseDown={(e) => e.stopPropagation()}
              placeholder={t('dropdown.searchPlaceholder')}
              className="input input-sm input-bordered w-full"
            />
          </div>
          {filtered.length === 0 && (
            <p className="px-4 py-2 text-sm text-base-content/40 cursor-default select-none">
              {t('common.noResults')}
            </p>
          )}
          <ul {...listbox.getListboxProps()} className="max-h-52 overflow-y-auto">
            {filtered.map((o, index) => (
              <li
                key={o.value}
                {...listbox.getOptionProps(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(o.value)}
                className={`px-4 py-2 text-sm cursor-pointer hover:bg-base-200 flex items-center gap-2 ${
                  listbox.isActive(index) ? 'bg-base-200' : ''
                }`}
              >
                <span className="text-base-content/40 font-mono text-sm w-7 shrink-0">
                  {o.value}
                </span>
                <span>{o.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function CountryMultiselectField({ name, options = [] }: BaseFieldProps) {
  const { control } = useFormContext();
  const { isReadOnly } = useFormMode();
  const countryOptions = options as FieldOption[];

  const labelFor = (iso: string) => countryOptions.find((o) => o.value === iso)?.label ?? iso;

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field }) => {
        const values: string[] = Array.isArray(field.value) ? field.value : [];
        return (
          <FieldWrapper>
            {isReadOnly ? (
              <div className="flex flex-wrap gap-2 py-2">
                {values.length > 0 ? (
                  values.map((iso) => <Pill key={iso} label={labelFor(iso)} />)
                ) : (
                  <span className="text-base-content/60">—</span>
                )}
              </div>
            ) : (
              <CountryMultiselectEditor
                values={values}
                onChange={field.onChange}
                options={countryOptions}
                labelFor={labelFor}
              />
            )}
          </FieldWrapper>
        );
      }}
    />
  );
}
